import { Button, Input, MOBILE_MAX_SIZE, Modal, Toggle, useWindowSize } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useState } from 'react';
import validators from '@sendible/common/utils/validators';
import { Container, Row, ColorBox, Group, Hr, Title, Content, DateArea, Preview } from './index.styles';
import { Calendar, ColorPicker, defaultColor } from './components';
import { useCampaignsContext } from '../../context';

type UpdateStateObject = {
  name?: string;
  color?: string;
};

const MAX_NAME_LENGTH = 50;

export const NewCampaignModal = () => {
  const { t } = useTranslation(['campaigns', 'common_terms', 'sendible360']);
  const { activeCampaignObject, isCampaignSaving, setIsEditionModalOpen, isEditionModalOpen, saveCampaignFromObject } = useCampaignsContext();
  const isEditMode = typeof isEditionModalOpen === 'number';

  const [isInputError, setIsInputError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(t('campaign_name_error'));
  const { width } = useWindowSize();

  // ── Render-Phase State Synchronization ──
  // Compute the current local campaign draft cleanly across active changes inline
  const [currentCampaign, setCurrentCampaign] = useState<CampaignsData>(() =>
    isEditMode ? activeCampaignObject : ({ color: defaultColor } as CampaignsData)
  );
  const [prevActiveCampaign, setPrevActiveCampaign] = useState<CampaignsData | null>(activeCampaignObject || null);

  if (isEditMode && activeCampaignObject !== prevActiveCampaign) {
    setPrevActiveCampaign(activeCampaignObject);
    setCurrentCampaign(activeCampaignObject);
  }

  // ── Pure Render-Phase State Derivation ──
  // Calculate valid state directly on every frame instead of a layout synchronization effect loop
  const isValidName = currentCampaign.name && currentCampaign.name.trim() !== '';
  const endDateNotDefined = !currentCampaign.endDate;
  const isEmptyDates = !currentCampaign.startDate && endDateNotDefined;
  const isValidRageDates =
    currentCampaign.startDate &&
    currentCampaign.endDate &&
    currentCampaign.startDate !== null &&
    currentCampaign.endDate !== null &&
    currentCampaign.startDate <= currentCampaign.endDate;

  const disableToSave = !(isValidName && (isEmptyDates || endDateNotDefined || isValidRageDates));

  const settingsSaveButtonText = t('settings_save_button');
  const modalTitle = isEditMode ? `${t('dropdown_edit')} ${currentCampaign.name}` : t('sibebar_new_campaigns_button');
  const savingLabel = `${t('saving', { ns: 'common_terms' })}...`;
  const activeLabel = t('active');
  const saveButtonLabel = isCampaignSaving ? savingLabel : settingsSaveButtonText;
  const isSaveButtonDisabled = isCampaignSaving || isInputError || disableToSave;

  const handleValueChange = (campaignSettings: UpdateStateObject) => {
    const isOverMaxLength = campaignSettings.name && campaignSettings.name.trim().length > MAX_NAME_LENGTH;
    const isNotAlphaNumeric = campaignSettings.name && !validators.extendedAlphanumeric(campaignSettings.name);

    if (campaignSettings.name && (isOverMaxLength || isNotAlphaNumeric)) {
      setIsInputError(true);

      const errorMessageText = isOverMaxLength ? t('campaign_name_error') : t('campaign_name_invalid_chars_error', { ns: 'sendible360' });

      setErrorMessage(errorMessageText);

      return;
    }

    setErrorMessage('');

    setIsInputError(false);

    setCurrentCampaign((prev) => ({
      ...prev,
      ...campaignSettings,
    }));
  };

  return (
    <Modal
      close={() => {
        setIsEditionModalOpen(false);
      }}
      variation={width > MOBILE_MAX_SIZE ? 'large' : 'fullscreen'}
      headingTitle={modalTitle}
      button={{
        action: () => saveCampaignFromObject(currentCampaign),
        actionLabel: saveButtonLabel,
        isDisabled: isSaveButtonDisabled,
        isLoading: isCampaignSaving,
      }}
    >
      <Container>
        <Content>
          <Group>
            <Row $alignment="flex-start">
              <Input
                initialValue={currentCampaign.name}
                feedback={isInputError ? { type: 'danger', message: errorMessage } : undefined}
                label={t('input_name_placeholder')}
                change={(value) => {
                  handleValueChange({
                    name: value,
                  });
                }}
              />
              {isEditMode && (
                <Row $height="2.5rem">
                  <Toggle
                    label={width > MOBILE_MAX_SIZE ? activeLabel : undefined}
                    size="sm"
                    checked={currentCampaign.status == 'active'}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      setCurrentCampaign((prev) => ({
                        ...prev,
                        status: event.target.checked ? 'active' : 'inactive',
                      }))
                    }
                  />
                </Row>
              )}
            </Row>
            <Hr />
          </Group>
          <Group>
            <Row>
              <ColorBox>
                <Title
                  semantic="h6"
                  variation="component-title_16"
                >
                  {t('color')}
                </Title>
                <ColorPicker
                  currentColor={currentCampaign.color || defaultColor}
                  onColorSelect={(newColor) =>
                    setCurrentCampaign((prev) => ({
                      ...prev,
                      color: newColor,
                    }))
                  }
                />
              </ColorBox>
              <ColorBox>
                <Title
                  semantic="h6"
                  variation="component-title_16"
                >
                  {t('label_preview')}
                </Title>
                <Preview
                  data-testid="campaign-label-preview"
                  $color={currentCampaign.color || defaultColor}
                >
                  <span>{currentCampaign.name || t('label')}</span>
                </Preview>
              </ColorBox>
            </Row>
            <Hr />
          </Group>
          <Group>
            <DateArea>
              <Title
                semantic="h6"
                variation="component-title_16"
              >
                {t('daterange_heading')}
              </Title>
              <Row>
                <Calendar
                  currentCampaign={currentCampaign}
                  setCurrentCampaign={setCurrentCampaign}
                />
              </Row>
            </DateArea>
          </Group>
        </Content>
        {width > MOBILE_MAX_SIZE && (
          <Button
            appearance="primary"
            label={saveButtonLabel}
            isLoading={isCampaignSaving}
            variation="fill"
            disabled={isCampaignSaving || isInputError || disableToSave}
            onClick={() => {
              saveCampaignFromObject(currentCampaign);
            }}
            size={18}
          />
        )}
      </Container>
    </Modal>
  );
};
